import moment from "moment";
import { FC, useState } from "react";
import { Alert, Button, Col, Modal, Pagination, Row, Spinner, Table } from "react-bootstrap";
import { useAccount, useMsal } from "@azure/msal-react";
import { queryOrders, revertExportOrder } from "../../app/services/orderService";
import { Order } from "../../models/responses/order";
import { RootState } from "../../app/store";
import { useSelector } from "react-redux";
import { ArrowClockwise, Check } from "react-bootstrap-icons";
import Form from "react-bootstrap/Form";
import { useAppDispatch } from "../../app/hooks";
import AdminSubmenu from "../../features/adminSubmenu";
import { ClientType } from "../../models/enums/clientTypeEnum";

interface Props { }

const AdminOrders: FC<Props> = () => {
    moment.locale('sv');
    moment.updateLocale(moment.locale(), { invalidDate: "-" })
    const dispatch = useAppDispatch();
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [showRevertExportModal, setShowRevertExportModel] = useState(false);
    const handleCloseRevertExportModal = () => setShowRevertExportModel(false);
    const [displayError, setDisplayError] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const loadingOrders: boolean = useSelector(
        (state: RootState) => state.order.isWaitingOrderGetResponse
    );
    const orders: Order[] = useSelector(
        (state: RootState) => state.order.queryOrderList || []
    );

    const [searchInput, setSearchInput] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const total = useSelector((state: RootState) => state.order.totalQueryOrder);
    const totalPages = Math.ceil((total || 0) / pageSize);
    const clientType = useSelector(
        (state: RootState) => state.clientType.clientType
    );
    const clientTypeLabel =
        clientType === ClientType.Standard ? "Skadeverkstad" : "AD Butik";

    const handleSearch = () => {
        dispatch(queryOrders(instance, account, undefined, page, pageSize, searchInput, "CreatedAt", true, false, clientType));
    };

    const handleKeyDown = (e: { key: string; preventDefault: () => void; }) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };
    const handleShowRevertExportModal = (order: Order) => {
        setSelectedOrder(order);
        setShowRevertExportModel(true);
    };

    const handleRevertExportOrder = (order: Order) => {
        setDisplayError(false);
        setShowRevertExportModel(false);
        dispatch(revertExportOrder(instance, account, order.id, onSuccess));
    };

    const onSuccess = (orderId: string | null) => {
        if (orderId) {
            dispatch(queryOrders(instance, account, undefined, page, pageSize, searchInput, "CreatedAt", true, false, clientType));
        } else {
            setDisplayError(true);
        }
        setShowRevertExportModel(false);
    };

    return (
        <div>
            <AdminSubmenu />
            <div>
                <h2 className="mb-3">Sök order för att återställa export</h2>
                <p>
                    Här kan du återställa export av order som är skapade i AX.
                    <ul>
                        <li>Ta bort/annullera ev inköpsorders rader i AX</li>
                        <li>Ta bort/annullera ev försäljningsorder rader i AX</li>
                        <li>Använd denna funktion för att markera ordern som "Ej exporterad" i AD One</li>
                    </ul>
                </p>
                <Form className="mb-3">
                    <Row>
                        <Col xs={12} md={4}>
                            <Form.Control
                                type="text"
                                placeholder="Regnummer/IO-nummer"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </Col>
                        <Col xs={12} md={2}>
                            <Button variant="outline-primary" onClick={handleSearch}>
                                Sök
                            </Button>
                        </Col>
                    </Row>
                </Form>
                <div>
                    <Table responsive striped bordered hover>
                        <thead>
                            <tr role="button">
                                <th>Skapad</th>
                                <th>Orderstatus</th>
                                <th>Kund</th>
                                <th>Reg.nr</th>
                                <th>AO-nummer</th>
                                <th>IO-nummer</th>
                                <th>Exporterad</th>
                                <th>Återställ export</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => (
                                <tr
                                    key={order.id}

                                >
                                    <td>{moment.utc(order.createdAt).tz('Europe/Stockholm').format("yyyy-MM-DD HH:mm")}</td>
                                    <td>{order.orderStatus?.name}</td>
                                    <td>{order.customer?.name}</td>
                                    <td>{order.registrationNumber}</td>
                                    <td>{order.aoNumber}</td>
                                    <td>{order.purchaseOrderNumber}</td>
                                    <td>{order.exported && <Check className="text-success" />}</td>
                                    <td
                                        role="button"
                                        onClick={() => handleShowRevertExportModal(order)}
                                        align="center">
                                        <ArrowClockwise />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {loadingOrders && (
                        <>
                            <Row className="mb-3">
                                <Col className="text-center">
                                    <Spinner variant="primary" />
                                </Col>
                            </Row>
                        </>
                    )}
                    <Pagination className="justify-content-center">
                        <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                        <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 1} />
                        {[...Array(totalPages)].map((_, index) => {
                            const pageIndex = index + 1;
                            if (pageIndex >= page - 3 && pageIndex <= page + 3) {
                                return (
                                    <Pagination.Item
                                        key={pageIndex}
                                        active={pageIndex === page}
                                        onClick={() => setPage(pageIndex)}
                                    >
                                        {pageIndex}
                                    </Pagination.Item>
                                );
                            }
                            return null;
                        })}
                        <Pagination.Next onClick={() => setPage(page + 1)} disabled={page === totalPages} />
                        <Pagination.Last onClick={() => setPage(totalPages)} disabled={page === totalPages} />
                    </Pagination>
                </div>

                <Alert
                    key="alert"
                    variant="danger"
                    show={displayError}
                    className="mt-3"
                >
                    <p>Något gick fel vid återställning av exporten till AX, vänligen försök igen.</p>
                </Alert>
            </div>
          
            <Modal show={showRevertExportModal} onHide={handleCloseRevertExportModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Återställ export av order till AX</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Detta kommer att markera ordern som "Ej exporterad" i AD One.
                        <br />
                        Säkerställ att allt är rätt i AX innan du gör detta.
                        <br />
                        <strong>Det här gör inte att ordern tas bort i AX.</strong>
                    </p>
                    <p><strong>Id:</strong> {selectedOrder?.id}</p>
                    <p><strong>Regnr:</strong> {selectedOrder?.registrationNumber}</p>
                    <p><strong>AO-nummer:</strong> {selectedOrder?.aoNumber}</p>
                    <p><strong>Kund:</strong> {selectedOrder?.customer?.name}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseRevertExportModal}>
                        Avbryt
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => handleRevertExportOrder(selectedOrder!)}
                    >
                        Återställ export i ADOne
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminOrders;
